import { useState, useEffect, useRef, useMemo, useCallback, type FormEvent } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import * as maplibregl from 'maplibre-gl';
import type { Feature } from 'geojson';
import {
  MapPin, Search, Map as MapIcon, List, X, ChevronDown, ChevronRight,
  Heart, Truck, RotateCcw, SlidersHorizontal, Lock,
} from 'lucide-react';
import { useAppAuth } from '../hooks/useAppAuth';
import { jobService, createApiClient, profileService, favouriteJobsService } from '../services/api';
import { useFleetHub, type FleetLocationUpdate } from '../hooks/useSignalR';
import { JobType, VehicleType } from '../types';
import type { JobSummaryDto, JobDetailDto, UserProfileDto } from '../types';
import { MAP_STYLE_URL, createPinElement } from '../lib/mapLibre';

// ─────────────────────────────────────────────
//  CONSTANTS
// ─────────────────────────────────────────────
const JOB_COLOURS = [
  '#ef4444','#3b82f6','#22c55e','#f59e0b','#8b5cf6',
  '#ec4899','#06b6d4','#84cc16','#f97316','#6366f1',
  '#14b8a6','#f43f5e','#0ea5e9','#a3e635','#fb923c',
  '#a855f7','#10b981','#fbbf24','#64748b','#e11d48',
  '#0284c7','#16a34a','#d97706','#7c3aed','#0f766e',
];

type SortKey =
  | 'distance-asc' | 'distance-desc'
  | 'newest'       | 'oldest'
  | 'route-asc'    | 'route-desc'
  | 'bids-asc'     | 'bids-desc';

const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: 'distance-asc',  label: 'Nearest pickup first'  },
  { value: 'distance-desc', label: 'Furthest pickup first' },
  { value: 'newest',        label: 'Newest posted'         },
  { value: 'oldest',        label: 'Oldest posted'         },
  { value: 'route-asc',     label: 'Shortest route'        },
  { value: 'route-desc',    label: 'Longest route'         },
  { value: 'bids-asc',      label: 'Fewest bids'           },
  { value: 'bids-desc',     label: 'Most bids'             },
];

const JOB_TYPE_LABELS: Record<JobType, string> = {
  [JobType.SameDayDelivery]:         'Same-Day',
  [JobType.ScheduledDelivery]:       'Scheduled',
  [JobType.RelayDelivery]:           'Relay',
  [JobType.VehicleRepair]:           'Repair',
  [JobType.Removal]:                 'Removal',
  [JobType.FreightHaulage]:          'Haulage',
  [JobType.MultiDropDelivery]:       'Multi-Drop',
  [JobType.MultiPickupDropDelivery]: 'Multi-Pickup-Drop',
};

const VEHICLE_LABELS: Record<VehicleType, string> = {
  [VehicleType.Motorcycle]: 'Motorcycle',
  [VehicleType.Car]:        'Car',
  [VehicleType.SmallVan]:   'Small Van',
  [VehicleType.MidiVan]:    'Midi Van',
  [VehicleType.LWBVan]:     'LWB Van',
  [VehicleType.LutonVan]:   'Luton Van',
  [VehicleType.Tonne7_5]:   '7.5T',
  [VehicleType.Tonne12]:    '12T',
  [VehicleType.Tonne18]:    '18T',
  [VehicleType.Tonne26]:    '26T',
  [VehicleType.Artic]:      'Artic',
};

const RADIUS_OPTIONS = [5, 10, 15, 25, 50, 100, 150, 200];

const ALL_JOB_TYPES: JobType[] = [
  JobType.SameDayDelivery,
  JobType.ScheduledDelivery,
  JobType.RelayDelivery,
  JobType.FreightHaulage,
  JobType.Removal,
  JobType.VehicleRepair,
  JobType.MultiDropDelivery,
  JobType.MultiPickupDropDelivery,
];

// ─────────────────────────────────────────────
//  HELPERS
// ─────────────────────────────────────────────
async function geocodePostcode(postcode: string): Promise<{ lat: number; lng: number } | null> {
  try {
    const r = await fetch(`https://api.postcodes.io/postcodes/${encodeURIComponent(postcode.trim())}`);
    const d = await r.json();
    if (d.result?.latitude) return { lat: d.result.latitude, lng: d.result.longitude };
  } catch {}
  return null;
}

function sortJobs(jobs: JobSummaryDto[], key: SortKey): JobSummaryDto[] {
  return [...jobs].sort((a, b) => {
    switch (key) {
      case 'distance-asc':  return (a.distanceFromCarrierMiles ?? 0) - (b.distanceFromCarrierMiles ?? 0);
      case 'distance-desc': return (b.distanceFromCarrierMiles ?? 0) - (a.distanceFromCarrierMiles ?? 0);
      case 'newest':        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      case 'oldest':        return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      case 'route-asc':     return a.distanceMiles - b.distanceMiles;
      case 'route-desc':    return b.distanceMiles - a.distanceMiles;
      case 'bids-asc':      return a.quoteCount - b.quoteCount;
      case 'bids-desc':     return b.quoteCount - a.quoteCount;
      default:              return 0;
    }
  });
}

function timeAgo(ts: string): string {
  const secs = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
  if (secs < 60)   return 'just now';
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  return `${Math.floor(secs / 3600)}h ago`;
}

function vanIconHtml(color: string, stale: boolean): string {
  const op = stale ? '0.5' : '1';
  return `<div style="opacity:${op};background:${color};border-radius:5px;padding:3px 5px;display:inline-flex;align-items:center;box-shadow:0 2px 5px rgba(0,0,0,.35)">
    <svg width="20" height="13" viewBox="0 0 20 13" fill="white" xmlns="http://www.w3.org/2000/svg">
      <rect y="1.5" width="12" height="9" rx="1.5"/>
      <rect x="12" y="3" width="7" height="7" rx="1"/>
      <rect x="12.5" y="3.5" width="6" height="3" fill="${color}" opacity="0.6"/>
      <circle cx="3.5" cy="11.5" r="2.2" stroke="white" stroke-width="1.5" fill="${color}"/>
      <circle cx="15.5" cy="11.5" r="2.2" stroke="white" stroke-width="1.5" fill="${color}"/>
    </svg>
  </div>`;
}

// ─────────────────────────────────────────────
//  TYPES
// ─────────────────────────────────────────────
interface CarrierMarker {
  carrierId:     string;
  carrierName:   string;
  jobId?:        string | null;
  jobReference?: string | null;
  lat:           number;
  lng:           number;
  accuracy?:     number;
  recordedAt:    string;
  stale:         boolean;
}

// ─────────────────────────────────────────────
//  MAP COMPONENT
// ─────────────────────────────────────────────
interface AvailableJobsMapProps {
  jobDetails:    JobDetailDto[];
  lockedIds:     Set<string>;
  onToggleLock:  (id: string) => void;
  employees:     CarrierMarker[];
  showEmployees: boolean;
}

const ROUTES_SOURCE = 'available-jobs-routes';
const ROUTES_LAYER = 'available-jobs-routes-line';
const ROUTES_HITBOX_LAYER = 'available-jobs-routes-hitbox';

function AvailableJobsMap({ jobDetails, lockedIds, onToggleLock, employees, showEmployees }: AvailableJobsMapProps) {
  const containerRef    = useRef<HTMLDivElement>(null);
  const mapRef          = useRef<maplibregl.Map | null>(null);
  const [loaded, setLoaded] = useState(false);
  const markersRef      = useRef<Map<string, { pm: maplibregl.Marker; dm: maplibregl.Marker }>>(new Map());
  const empMarkersRef   = useRef<Map<string, maplibregl.Marker>>(new Map());
  const hoveredJobIdRef = useRef<string | null>(null);
  const tooltipRef      = useRef<maplibregl.Popup | null>(null);
  const onToggleLockRef = useRef(onToggleLock);
  onToggleLockRef.current = onToggleLock;
  const lockedIdsRef    = useRef(lockedIds);
  lockedIdsRef.current  = lockedIds;
  const jobDetailsRef   = useRef(jobDetails);
  jobDetailsRef.current = jobDetails;

  const tooltipHtmlFor = (job: JobDetailDto, locked: boolean) =>
    `<div style="min-width:190px;font-size:12px;line-height:1.5">` +
    `<div style="font-weight:700">${job.referenceNumber}</div>` +
    `<div style="color:#374151">${job.title}</div>` +
    `<div style="color:#6b7280;margin-top:3px">` +
      `📍 ${job.pickupAddress.split(',')[0].trim()} → ${job.deliveryAddress.split(',')[0].trim()}` +
    `</div>` +
    `<div style="color:#6b7280">${job.distanceMiles.toFixed(1)} mi · ${job.quoteCount} bid${job.quoteCount !== 1 ? 's' : ''}</div>` +
    `<div style="color:#9ca3af;font-size:11px;margin-top:3px">Double-click to ${locked ? 'unlock' : 'lock'} route</div>` +
    `</div>`;

  const toggleLock = useCallback((jobId: string) => onToggleLockRef.current(jobId), []);

  // Initialise map once
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const map = new maplibregl.Map({
      container: containerRef.current,
      style: MAP_STYLE_URL,
      center: [-1.5, 52.5],
      zoom: 6,
    });
    mapRef.current = map;
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }));
    tooltipRef.current = new maplibregl.Popup({ closeButton: false, closeOnClick: false, offset: 10 });

    map.on('load', () => {
      map.addSource(ROUTES_SOURCE, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });

      // Invisible wide line purely for reliable hover/click hit-testing - the visible line is
      // often only 2px/30%-opacity, which would otherwise be an unreasonably small hit target.
      map.addLayer({
        id: ROUTES_HITBOX_LAYER, type: 'line', source: ROUTES_SOURCE,
        paint: { 'line-color': '#000', 'line-width': 16, 'line-opacity': 0 },
      });
      map.addLayer({
        id: ROUTES_LAYER, type: 'line', source: ROUTES_SOURCE,
        layout: { 'line-cap': 'round' },
        paint: {
          'line-color': ['get', 'color'],
          'line-width': [
            'case',
            ['boolean', ['feature-state', 'hover'], false], 4,
            ['get', 'locked'], 4, 2,
          ],
          'line-opacity': [
            'case',
            ['boolean', ['feature-state', 'hover'], false], 0.85,
            ['get', 'locked'], 0.9, 0.3,
          ],
          'line-dasharray': [
            'case',
            ['any', ['boolean', ['feature-state', 'hover'], false], ['get', 'locked']], ['literal', [1, 0]],
            ['literal', [2.5, 1.5]],
          ],
        },
      });

      map.on('mousemove', ROUTES_HITBOX_LAYER, (e: maplibregl.MapLayerMouseEvent) => {
        const feature = e.features?.[0];
        if (!feature) return;
        const jobId = feature.properties?.jobId as string;

        if (hoveredJobIdRef.current !== jobId) {
          if (hoveredJobIdRef.current)
            map.setFeatureState({ source: ROUTES_SOURCE, id: hoveredJobIdRef.current }, { hover: false });
          map.setFeatureState({ source: ROUTES_SOURCE, id: jobId }, { hover: true });
          hoveredJobIdRef.current = jobId;
        }

        const job = jobDetailsRef.current.find(j => j.id === jobId);
        if (job) {
          tooltipRef.current
            ?.setLngLat(e.lngLat)
            .setHTML(tooltipHtmlFor(job, lockedIdsRef.current.has(jobId)))
            .addTo(map);
        }
        map.getCanvas().style.cursor = 'pointer';
      });

      map.on('mouseleave', ROUTES_HITBOX_LAYER, () => {
        if (hoveredJobIdRef.current) {
          map.setFeatureState({ source: ROUTES_SOURCE, id: hoveredJobIdRef.current }, { hover: false });
          hoveredJobIdRef.current = null;
        }
        tooltipRef.current?.remove();
        map.getCanvas().style.cursor = '';
      });

      map.on('dblclick', ROUTES_HITBOX_LAYER, (e: maplibregl.MapLayerMouseEvent) => {
        const jobId = e.features?.[0]?.properties?.jobId as string | undefined;
        if (jobId) { e.preventDefault(); toggleLock(jobId); }
      });

      setLoaded(true);
    });

    return () => {
      setLoaded(false);
      tooltipRef.current?.remove();
      mapRef.current?.remove();
      mapRef.current = null;
      markersRef.current.clear();
      empMarkersRef.current.clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Draw / update job layers whenever jobDetails list changes
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !loaded) return;

    const currentIds = new Set(jobDetails.map(j => j.id));
    markersRef.current.forEach(({ pm, dm }, id) => {
      if (!currentIds.has(id)) {
        pm.remove(); dm.remove();
        markersRef.current.delete(id);
      }
    });

    const allCoords: [number, number][] = [];
    const features: Feature[] = jobDetails.map((job, idx) => {
      const color = JOB_COLOURS[idx % JOB_COLOURS.length];
      const locked = lockedIdsRef.current.has(job.id);
      allCoords.push([job.pickupLng, job.pickupLat], [job.deliveryLng, job.deliveryLat]);

      if (!markersRef.current.has(job.id)) {
        const pm = new maplibregl.Marker({ element: createPinElement('P', color) })
          .setLngLat([job.pickupLng, job.pickupLat]).addTo(map);
        const dm = new maplibregl.Marker({ element: createPinElement('D', color) })
          .setLngLat([job.deliveryLng, job.deliveryLat]).addTo(map);
        [pm, dm].forEach(m => m.getElement().addEventListener('dblclick', (e: MouseEvent) => {
          e.stopPropagation();
          toggleLock(job.id);
        }));
        markersRef.current.set(job.id, { pm, dm });
      }
      const { pm, dm } = markersRef.current.get(job.id)!;
      pm.getElement().style.opacity = locked ? '1' : '0.4';
      dm.getElement().style.opacity = locked ? '1' : '0.4';

      return {
        type: 'Feature', id: job.id, properties: { jobId: job.id, color, locked },
        geometry: {
          type: 'LineString',
          coordinates: [[job.pickupLng, job.pickupLat], [job.deliveryLng, job.deliveryLat]],
        },
      };
    });

    (map.getSource(ROUTES_SOURCE) as maplibregl.GeoJSONSource)?.setData({ type: 'FeatureCollection', features });

    if (allCoords.length > 0) {
      const bounds = allCoords.reduce(
        (b, c) => b.extend(c), new maplibregl.LngLatBounds(allCoords[0], allCoords[0]));
      map.fitBounds(bounds, { padding: 40 });
      setTimeout(() => map.resize(), 200);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobDetails, loaded]);

  // Re-style lines/pins whenever the locked set changes (feature `locked` property drives paint)
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !loaded) return;

    const source = map.getSource(ROUTES_SOURCE) as maplibregl.GeoJSONSource | undefined;
    if (!source) return;

    source.setData({
      type: 'FeatureCollection',
      features: jobDetails.map((job, idx) => ({
        type: 'Feature', id: job.id,
        properties: { jobId: job.id, color: JOB_COLOURS[idx % JOB_COLOURS.length], locked: lockedIds.has(job.id) },
        geometry: {
          type: 'LineString',
          coordinates: [[job.pickupLng, job.pickupLat], [job.deliveryLng, job.deliveryLat]],
        },
      })),
    });

    markersRef.current.forEach(({ pm, dm }, jobId) => {
      const locked = lockedIds.has(jobId);
      pm.getElement().style.opacity = locked ? '1' : '0.4';
      dm.getElement().style.opacity = locked ? '1' : '0.4';
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lockedIds]);

  // Update / create employee van markers
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !loaded) return;

    const seen = new Set<string>();

    if (showEmployees) {
      employees.forEach(emp => {
        seen.add(emp.carrierId);
        const isFree = !emp.jobId;
        const color  = emp.stale ? '#94a3b8' : isFree ? '#2563eb' : '#dc2626';
        const popupHtml =
          `<div style="min-width:150px;font-size:12px">` +
          `<div style="font-weight:700;margin-bottom:3px">${emp.carrierName}</div>` +
          (isFree
            ? `<div style="color:#16a34a;font-weight:600">● Available</div>`
            : `<div style="color:#dc2626;font-weight:600">● On job: ${emp.jobReference ?? 'Active'}</div>`) +
          `<div style="color:#94a3b8;font-size:11px;margin-top:2px">Last seen: ${timeAgo(emp.recordedAt)}</div>` +
          `</div>`;

        const existing = empMarkersRef.current.get(emp.carrierId);
        if (existing) {
          existing.setLngLat([emp.lng, emp.lat]);
          existing.getElement().innerHTML = vanIconHtml(color, emp.stale);
          existing.getPopup()?.setHTML(popupHtml);
        } else {
          const el = document.createElement('div');
          el.innerHTML = vanIconHtml(color, emp.stale);
          const marker = new maplibregl.Marker({ element: el })
            .setLngLat([emp.lng, emp.lat])
            .setPopup(new maplibregl.Popup({ offset: 14 }).setHTML(popupHtml))
            .addTo(map);
          empMarkersRef.current.set(emp.carrierId, marker);
        }
      });
    }

    empMarkersRef.current.forEach((m, id) => {
      if (!seen.has(id)) { m.remove(); empMarkersRef.current.delete(id); }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employees, showEmployees, loaded]);

  return <div ref={containerRef} className="w-full h-full" />;
}

// ─────────────────────────────────────────────
//  MAP LEGEND OVERLAY
// ─────────────────────────────────────────────
function MapLegend({ lockedCount, freeCount, onJobCount }: {
  lockedCount: number; freeCount: number; onJobCount: number;
}) {
  if (lockedCount === 0 && freeCount === 0 && onJobCount === 0) return null;
  return (
    <div className="absolute bottom-4 left-4 z-[1000] bg-white/95 backdrop-blur-sm rounded-xl shadow-lg border border-gray-100 px-3 py-2 flex flex-wrap gap-3 text-xs">
      {lockedCount > 0 && (
        <span className="flex items-center gap-1.5 text-gray-700">
          <Lock className="w-3 h-3" />
          {lockedCount} locked route{lockedCount !== 1 ? 's' : ''}
        </span>
      )}
      {freeCount > 0 && (
        <span className="flex items-center gap-1.5 text-gray-700">
          <span className="w-3.5 h-2.5 rounded-sm bg-blue-600 inline-block" />
          {freeCount} available
        </span>
      )}
      {onJobCount > 0 && (
        <span className="flex items-center gap-1.5 text-gray-700">
          <span className="w-3.5 h-2.5 rounded-sm bg-red-600 inline-block" />
          {onJobCount} on job
        </span>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
//  MAIN PAGE
// ─────────────────────────────────────────────
export default function JobMapPage() {
  const { getToken, account } = useAppAuth();
  const queryClient = useQueryClient();

  // ── View / sidebar ──────────────────────────
  const [view, setView]           = useState<'list' | 'map'>('list');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // ── FROM search ─────────────────────────────
  const [postcode, setPostcode]         = useState('');
  const [radius, setRadius]             = useState(25);
  const [coords, setCoords]             = useState<{ lat: number; lng: number } | null>(null);
  const [searchedPostcode, setSearchedPostcode] = useState('');
  const [postcodeError, setPostcodeError] = useState('');
  const [geocoding, setGeocoding]       = useState(false);

  // ── Filters ─────────────────────────────────
  const [sortKey, setSortKey]           = useState<SortKey>('distance-asc');
  const [toFilter, setToFilter]         = useState('');
  const [vehicleMin, setVehicleMin]     = useState<VehicleType>(VehicleType.Car);
  const [vehicleMax, setVehicleMax]     = useState<VehicleType>(VehicleType.Artic);
  const [enabledJobTypes, setEnabledJobTypes] = useState<Set<JobType>>(new Set(ALL_JOB_TYPES));
  const [favOnly, setFavOnly]           = useState(false);
  const [sameDayOnly, setSameDayOnly]   = useState(false);
  const [showEmployees, setShowEmployees] = useState(true);

  // ── Map state ────────────────────────────────
  const [lockedJobIds, setLockedJobIds] = useState<Set<string>>(new Set());
  const jobDetailCacheRef               = useRef<Map<string, JobDetailDto>>(new Map());
  const [jobDetailCache, setJobDetailCache] = useState<Map<string, JobDetailDto>>(new Map());
  const [mapLoadingCount, setMapLoadingCount] = useState(0);
  const mapFetchedRef                   = useRef<Set<string>>(new Set());

  // ── Fleet / employee state ───────────────────
  const [liveDrivers, setLiveDrivers]   = useState<Map<string, CarrierMarker>>(new Map());

  // ── Profile (carrierId for fleet hub) ────────
  const { data: profile } = useQuery<UserProfileDto>({
    queryKey: ['profile'],
    queryFn:  () => profileService(createApiClient(getToken)).getProfile(),
    enabled:  !!account,
    staleTime: Infinity,
  });
  const carrierId = profile?.userId ?? '';

  // ── Fetch available jobs ─────────────────────
  const { data: jobs = [], isLoading, error } = useQuery({
    queryKey: ['jobs-map-feed', coords?.lat, coords?.lng, radius],
    queryFn:  () => jobService(createApiClient(getToken)).getAvailableJobs(coords!.lat, coords!.lng, radius),
    enabled:  !!coords,
  });

  // ── Filter + sort ────────────────────────────
  const displayedJobs = useMemo(() => {
    let list = jobs;
    if (favOnly) list = list.filter(j => j.isFavourited);
    if (sameDayOnly) list = list.filter(j => j.isSameDay);
    if (toFilter.trim()) {
      const q = toFilter.trim().toLowerCase();
      list = list.filter(j => j.deliveryAddress.toLowerCase().includes(q));
    }
    if (vehicleMin !== VehicleType.Car || vehicleMax !== VehicleType.Artic) {
      list = list.filter(j =>
        j.requiredVehicleType === undefined ||
        (j.requiredVehicleType >= vehicleMin && j.requiredVehicleType <= vehicleMax)
      );
    }
    list = list.filter(j => enabledJobTypes.has(j.jobType));
    return sortJobs(list, sortKey);
  }, [jobs, sortKey, toFilter, vehicleMin, vehicleMax, enabledJobTypes, favOnly, sameDayOnly]);

  // ── Fetch job detail for map display ─────────
  const fetchJobDetail = useCallback(async (id: string) => {
    if (jobDetailCacheRef.current.has(id) || mapFetchedRef.current.has(id)) return;
    mapFetchedRef.current.add(id);
    try {
      const job = await jobService(createApiClient(getToken)).getJob(id);
      jobDetailCacheRef.current.set(id, job);
      setJobDetailCache(new Map(jobDetailCacheRef.current));
    } catch {
      mapFetchedRef.current.delete(id); // allow retry
    }
  }, [getToken]);

  // Batch-fetch details whenever map tab is active
  useEffect(() => {
    if (view !== 'map' || !displayedJobs.length) return;
    const toFetch = displayedJobs.filter(j => !jobDetailCacheRef.current.has(j.id));
    if (!toFetch.length) return;
    setMapLoadingCount(toFetch.length);
    Promise.allSettled(toFetch.map(j => fetchJobDetail(j.id)))
      .then(() => setMapLoadingCount(0));
  }, [view, displayedJobs, fetchJobDetail]);

  // Resolved detail list for map (only jobs that have loaded)
  const mapJobDetails = useMemo(() =>
    displayedJobs
      .map((j, idx) => {
        const d = jobDetailCache.get(j.id);
        return d ? { ...d, _idx: idx } : null;
      })
      .filter((d): d is JobDetailDto & { _idx: number } => d !== null),
    [displayedJobs, jobDetailCache],
  );

  // ── Fleet hub ────────────────────────────────
  const loadFleetSnapshot = useCallback(async () => {
    if (!carrierId) return;
    try {
      const res = await createApiClient(getToken).get<{
        carrierId: string; carrierName: string;
        jobId?: string | null; jobReference?: string | null;
        latitude: number; longitude: number;
        accuracyMetres?: number; recordedAt: string;
      }[]>('/api/carrier/fleet/live-locations');
      setLiveDrivers(prev => {
        const next = new Map(prev);
        res.data.forEach(d => next.set(d.carrierId, {
          carrierId: d.carrierId, carrierName: d.carrierName,
          jobId: d.jobId ?? null, jobReference: d.jobReference ?? null,
          lat: d.latitude, lng: d.longitude,
          accuracy: d.accuracyMetres, recordedAt: d.recordedAt, stale: false,
        }));
        return next;
      });
    } catch {}
  }, [carrierId, getToken]);

  useEffect(() => { loadFleetSnapshot(); }, [loadFleetSnapshot]);

  const handleFleetLocation = useCallback((update: FleetLocationUpdate) => {
    setLiveDrivers(prev => {
      const next     = new Map(prev);
      const existing = prev.get(update.carrierId);
      next.set(update.carrierId, {
        carrierId:    update.carrierId,
        carrierName:  existing?.carrierName ?? 'Driver',
        jobId:        update.jobId ?? null,
        jobReference: update.jobId ? (existing?.jobReference ?? null) : null,
        lat: update.lat, lng: update.lng,
        accuracy: update.accuracy, recordedAt: update.recordedAt, stale: false,
      });
      return next;
    });
  }, []);

  useFleetHub({ getToken, carrierId, onCarrierLocationUpdated: handleFleetLocation, enabled: !!carrierId });

  const employeeList = useMemo(() =>
    Array.from(liveDrivers.values()).map(d => ({
      ...d, stale: new Date(d.recordedAt).getTime() < Date.now() - 10 * 60_000,
    })),
    [liveDrivers],
  );

  const freeCount  = employeeList.filter(e => !e.jobId && !e.stale).length;
  const onJobCount = employeeList.filter(e =>  e.jobId && !e.stale).length;

  // ── Postcode search ───────────────────────────
  const handleSearch = async (e: FormEvent) => {
    e.preventDefault();
    setPostcodeError('');
    setGeocoding(true);
    const result = await geocodePostcode(postcode);
    setGeocoding(false);
    if (result) {
      setCoords(result);
      setSearchedPostcode(postcode.toUpperCase().trim());
      setLockedJobIds(new Set());
      jobDetailCacheRef.current.clear();
      mapFetchedRef.current.clear();
      setJobDetailCache(new Map());
    } else {
      setPostcodeError('Postcode not found — try a full UK postcode, e.g. LS1 4AP');
    }
  };

  // ── Favourites (optimistic) ───────────────────
  const favMutation = useMutation({
    mutationFn: ({ jobId, currently }: { jobId: string; currently: boolean }) =>
      currently
        ? favouriteJobsService(createApiClient(getToken)).remove(jobId)
        : favouriteJobsService(createApiClient(getToken)).add(jobId),
    onMutate: async ({ jobId, currently }) => {
      const key = ['jobs-map-feed', coords?.lat, coords?.lng, radius] as const;
      await queryClient.cancelQueries({ queryKey: key });
      const prev = queryClient.getQueryData<JobSummaryDto[]>(key);
      queryClient.setQueryData<JobSummaryDto[]>(
        key,
        old => old?.map(j => j.id === jobId ? { ...j, isFavourited: !currently } : j) ?? [],
      );
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev)
        queryClient.setQueryData(['jobs-map-feed', coords?.lat, coords?.lng, radius], ctx.prev);
    },
  });

  const toggleFav = useCallback((jobId: string, currently: boolean, e: React.MouseEvent) => {
    e.stopPropagation();
    favMutation.mutate({ jobId, currently });
  }, [favMutation]);

  // ── Lock toggle ───────────────────────────────
  const toggleLock = useCallback((id: string) => {
    setLockedJobIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  // ── Reset all filters ─────────────────────────
  const resetFilters = useCallback(() => {
    setSortKey('distance-asc');
    setToFilter('');
    setVehicleMin(VehicleType.Car);
    setVehicleMax(VehicleType.Artic);
    setEnabledJobTypes(new Set(ALL_JOB_TYPES));
    setFavOnly(false);
    setSameDayOnly(false);
  }, []);

  // ─────────────────────────────────────────────
  //  RENDER
  // ─────────────────────────────────────────────
  return (
    <div className="flex h-[calc(100vh-64px)] overflow-hidden bg-gray-50">
      <h1 className="sr-only">Available Jobs</h1>

      {/* ───────────── Filter sidebar ───────────── */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/20 z-10 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside className={`
        ${sidebarOpen ? 'flex' : 'hidden'}
        flex-col w-72 shrink-0 bg-white border-r border-gray-200 overflow-y-auto
        fixed md:relative z-20 h-full top-0 left-0
      `}>
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between shrink-0">
          <span className="font-semibold text-sm text-gray-800">Filters</span>
          <button className="text-gray-400 hover:text-gray-600 p-1" onClick={() => setSidebarOpen(false)}>
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 px-4 py-4 space-y-5 overflow-y-auto">

          {/* Radius (when sidebar open) */}
          <div className="space-y-1.5">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Search Radius</p>
            <div className="relative">
              <select
                className="w-full appearance-none border border-gray-200 rounded-lg px-3 pr-8 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-300"
                value={radius}
                onChange={e => setRadius(Number(e.target.value))}
              >
                {RADIUS_OPTIONS.map(r => <option key={r} value={r}>{r} miles</option>)}
              </select>
              <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {postcodeError && <p className="text-xs text-red-600">{postcodeError}</p>}

          {/* TO / delivery area */}
          <div className="space-y-1.5">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Delivery area (optional)</p>
            <div className="relative">
              <MapPin className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
              <input
                className="w-full pl-8 pr-7 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                placeholder="Town, city or postcode"
                value={toFilter}
                onChange={e => setToFilter(e.target.value)}
              />
              {toFilter && (
                <button
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500"
                  onClick={() => setToFilter('')}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* Sort */}
          <div className="space-y-1.5">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Sort by</p>
            <div className="relative">
              <select
                className="w-full appearance-none border border-gray-200 rounded-lg px-3 pr-8 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-300"
                value={sortKey}
                onChange={e => setSortKey(e.target.value as SortKey)}
              >
                {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {/* Vehicle type range */}
          <div className="space-y-2">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Vehicle Type</p>
            <div className="grid grid-cols-2 gap-2">
              {(['Min', 'Max'] as const).map(which => (
                <div key={which}>
                  <p className="text-xs text-gray-400 mb-1">{which}</p>
                  <div className="relative">
                    <select
                      className="w-full appearance-none border border-gray-200 rounded-lg px-2 pr-6 py-1.5 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-blue-300"
                      value={which === 'Min' ? vehicleMin : vehicleMax}
                      onChange={e => {
                        const v = Number(e.target.value) as VehicleType;
                        which === 'Min' ? setVehicleMin(v) : setVehicleMax(v);
                      }}
                    >
                      {Object.entries(VEHICLE_LABELS).map(([k, lbl]) => (
                        <option key={k} value={k}>{lbl}</option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-1.5 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400 pointer-events-none" />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Job type checkboxes */}
          <div className="space-y-1.5">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Job Type</p>
            <div className="space-y-1">
              {ALL_JOB_TYPES.map(jt => (
                <label key={jt} className="flex items-center gap-2 cursor-pointer py-0.5 select-none">
                  <input
                    type="checkbox"
                    checked={enabledJobTypes.has(jt)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-3.5 h-3.5 shrink-0"
                    onChange={e => {
                      setEnabledJobTypes(prev => {
                        const next = new Set(prev);
                        e.target.checked ? next.add(jt) : next.delete(jt);
                        return next;
                      });
                    }}
                  />
                  <span className="text-sm text-gray-700">{JOB_TYPE_LABELS[jt]}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Misc toggles */}
          <div className="space-y-2 border-t border-gray-100 pt-4">
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                aria-label="Same-day only"
                checked={sameDayOnly}
                onChange={e => setSameDayOnly(e.target.checked)}
                className="rounded border-gray-300 text-orange-500 focus:ring-orange-400 w-3.5 h-3.5 shrink-0"
              />
              <span className="text-sm text-gray-700">Same-day only</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={favOnly}
                onChange={e => setFavOnly(e.target.checked)}
                className="rounded border-gray-300 text-amber-500 focus:ring-amber-400 w-3.5 h-3.5 shrink-0"
              />
              <span className="text-sm text-gray-700">Favourites only</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={showEmployees}
                onChange={e => setShowEmployees(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-3.5 h-3.5 shrink-0"
              />
              <span className="text-sm text-gray-700 flex items-center gap-1.5">
                <Truck className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                Show employees on map
              </span>
            </label>
          </div>

          <button
            onClick={resetFilters}
            className="w-full flex items-center justify-center gap-2 text-sm text-gray-500 hover:text-gray-700 border border-gray-200 rounded-lg py-2 transition-colors hover:bg-gray-50"
          >
            <RotateCcw className="w-3.5 h-3.5" /> Reset Filters
          </button>
        </div>
      </aside>

      {/* ───────────── Main content ─────────────── */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">

        {/* Header bar */}
        <div className="shrink-0 px-4 py-3 bg-white border-b border-gray-200 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <button
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 shrink-0"
              onClick={() => setSidebarOpen(o => !o)}
            >
              <SlidersHorizontal className="w-4 h-4" />
              Filters
            </button>
            {/* Always-visible postcode search */}
            <form onSubmit={handleSearch} className="flex items-center gap-2 flex-1 min-w-0">
              <div className="relative flex-1 min-w-0 max-w-xs">
                <MapPin className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
                <input
                  className="w-full pl-8 pr-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                  placeholder="Postcode, e.g. LS1 4AP"
                  value={postcode}
                  onChange={e => { setPostcode(e.target.value); setPostcodeError(''); }}
                />
              </div>
              <select
                className="hidden sm:block appearance-none border border-gray-200 rounded-lg px-3 pr-8 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-300"
                value={radius}
                onChange={e => setRadius(Number(e.target.value))}
              >
                {RADIUS_OPTIONS.map(r => <option key={r} value={r}>{r} mi</option>)}
              </select>
              <button
                type="submit"
                disabled={geocoding || !postcode.trim()}
                className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-3 py-1.5 rounded-lg text-sm font-medium transition-colors shrink-0"
              >
                <Search className="w-3.5 h-3.5" />
                {geocoding ? 'Searching…' : 'Search'}
              </button>
            </form>
            {coords && !isLoading && (
              <p className="text-xs text-gray-400 truncate shrink-0">
                {displayedJobs.length} job{displayedJobs.length !== 1 ? 's' : ''} · {radius} mi of {searchedPostcode}
                {view === 'map' && mapLoadingCount > 0 && (
                  <span className="text-blue-500 ml-1.5">· loading routes…</span>
                )}
              </p>
            )}
          </div>

          {/* List / Map toggle */}
          <div className="flex rounded-lg border border-gray-200 overflow-hidden shrink-0">
            <button
              onClick={() => setView('list')}
              className={`px-3 py-1.5 flex items-center gap-1.5 text-sm transition-colors ${view === 'list' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}
            >
              <List className="w-4 h-4" />
              <span className="hidden sm:inline">List</span>
            </button>
            <button
              onClick={() => setView('map')}
              className={`px-3 py-1.5 flex items-center gap-1.5 text-sm transition-colors ${view === 'map' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}
            >
              <MapIcon className="w-4 h-4" />
              <span className="hidden sm:inline">Map</span>
            </button>
          </div>
        </div>

        {/* ── No search yet ── */}
        {!coords && (
          <div className="flex-1 flex items-center justify-center text-center px-4">
            <div>
              <MapPin className="w-10 h-10 text-gray-200 mx-auto mb-3" />
              <p className="text-sm font-medium text-gray-500">Enter a postcode to find nearby jobs</p>
              <p className="text-xs text-gray-400 mt-1">
                Enter a postcode in the search bar above to find available jobs
              </p>
            </div>
          </div>
        )}

        {/* ── Loading skeleton ── */}
        {coords && isLoading && (
          <div className="flex-1 overflow-auto p-4 space-y-2">
            {[1,2,3,4,5].map(i => (
              <div key={i} className="bg-white rounded-xl border border-gray-100 p-3 animate-pulse flex gap-3">
                <div className="w-5 h-5 bg-gray-200 rounded shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-3.5 bg-gray-200 rounded w-1/3" />
                  <div className="h-3 bg-gray-200 rounded w-2/3" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── Error ── */}
        {coords && error && (
          <div className="flex-1 flex items-center justify-center p-6">
            <div className="text-sm text-red-600 flex items-center gap-2 bg-red-50 rounded-xl p-4 border border-red-100">
              <X className="w-4 h-4 shrink-0" /> {String(error)}
            </div>
          </div>
        )}

        {/* ── LIST VIEW ── */}
        {coords && !isLoading && !error && view === 'list' && (
          <div className="flex-1 overflow-auto">
            {displayedJobs.length === 0 ? (
              <div className="flex items-center justify-center h-full text-center">
                <div>
                  <MapPin className="w-8 h-8 text-gray-200 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">No jobs match your filters</p>
                  <button onClick={resetFilters} className="text-xs text-blue-600 hover:underline mt-1.5">
                    Reset filters
                  </button>
                </div>
              </div>
            ) : (
              <table className="w-full text-sm border-collapse">
                <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
                  <tr>
                    <th className="w-10 py-2.5" />
                    <th className="text-left px-3 py-2.5 font-medium text-gray-600 text-xs">Job</th>
                    <th className="text-left px-3 py-2.5 font-medium text-gray-600 text-xs hidden md:table-cell">Route</th>
                    <th className="text-right px-3 py-2.5 font-medium text-gray-600 text-xs hidden sm:table-cell">Distance</th>
                    <th className="text-right px-3 py-2.5 font-medium text-gray-600 text-xs hidden lg:table-cell">Bids</th>
                    <th className="text-left px-3 py-2.5 font-medium text-gray-600 text-xs hidden sm:table-cell">Type</th>
                    <th className="w-10 py-2.5" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {displayedJobs.map(job => (
                    <tr key={job.id} className="hover:bg-gray-50/60 transition-colors">

                      {/* Favourite */}
                      <td className="pl-3 py-3 text-center align-middle">
                        <button
                          onClick={e => toggleFav(job.id, !!job.isFavourited, e)}
                          className="p-1 rounded-md hover:bg-amber-50 transition-colors"
                          title={job.isFavourited ? 'Remove favourite' : 'Add favourite'}
                        >
                          <Heart className={`w-4 h-4 transition-colors ${
                            job.isFavourited
                              ? 'fill-amber-500 text-amber-500'
                              : 'text-gray-400 hover:text-amber-400'
                          }`} />
                        </button>
                      </td>

                      {/* Ref + title */}
                      <td className="px-3 py-3 max-w-0 align-middle">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="font-semibold text-gray-900 text-xs shrink-0">{job.referenceNumber}</span>
                          {job.isSameDay && <span className="badge-amber shrink-0">⚡</span>}
                        </div>
                        <p className="text-xs text-gray-500 truncate mt-0.5">{job.title}</p>
                      </td>

                      {/* Route */}
                      <td className="px-3 py-3 hidden md:table-cell align-middle">
                        <div className="flex items-center gap-1 text-xs text-gray-500">
                          <span className="truncate max-w-[90px]">{job.pickupAddress.split(',')[0]}</span>
                          <ChevronRight className="w-3 h-3 shrink-0 text-gray-300" />
                          <span className="truncate max-w-[90px]">{job.deliveryAddress.split(',')[0]}</span>
                        </div>
                      </td>

                      {/* Distance */}
                      <td className="px-3 py-3 text-right hidden sm:table-cell align-middle whitespace-nowrap">
                        <div className="text-xs font-medium text-gray-700">{job.distanceMiles.toFixed(1)} mi</div>
                        {job.distanceFromCarrierMiles !== undefined && (
                          <div className="text-xs text-gray-400">{job.distanceFromCarrierMiles.toFixed(1)} mi away</div>
                        )}
                      </td>

                      {/* Bids */}
                      <td className="px-3 py-3 text-right hidden lg:table-cell align-middle">
                        <span className="text-xs text-gray-500">{job.quoteCount}</span>
                      </td>

                      {/* Type badges */}
                      <td className="px-3 py-3 hidden sm:table-cell align-middle">
                        <div className="flex flex-col gap-0.5">
                          <span className="badge-gray whitespace-nowrap text-xs">{JOB_TYPE_LABELS[job.jobType]}</span>
                          {job.requiredVehicleType !== undefined && (
                            <span className="badge-blue whitespace-nowrap text-xs">{VEHICLE_LABELS[job.requiredVehicleType]}</span>
                          )}
                        </div>
                      </td>

                      {/* Detail link — explicit chevron only, never whole-row navigation */}
                      <td className="pr-3 py-3 text-center align-middle">
                        <Link
                          to={`/jobs/${job.id}`}
                          className="inline-flex p-1.5 rounded-md text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                          title="View job details"
                        >
                          <ChevronRight className="w-4 h-4" />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* ── MAP VIEW ── */}
        {coords && !isLoading && !error && view === 'map' && (
          <div className="flex-1 relative overflow-hidden">
            {displayedJobs.length === 0 ? (
              <div className="flex items-center justify-center h-full text-center">
                <div>
                  <MapPin className="w-8 h-8 text-gray-200 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">No jobs match your filters</p>
                  <button onClick={resetFilters} className="text-xs text-blue-600 hover:underline mt-1.5">
                    Reset filters
                  </button>
                </div>
              </div>
            ) : (
              <>
                <AvailableJobsMap
                  jobDetails={mapJobDetails}
                  lockedIds={lockedJobIds}
                  onToggleLock={toggleLock}
                  employees={employeeList}
                  showEmployees={showEmployees}
                />

                {/* Loading indicator (top-right) */}
                {mapLoadingCount > 0 && (
                  <div className="absolute top-3 right-3 z-[1000] bg-white/95 rounded-xl shadow-md border border-gray-100 px-3 py-2 text-xs text-blue-600 flex items-center gap-2">
                    <span className="w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin shrink-0" />
                    Loading {mapLoadingCount} route{mapLoadingCount !== 1 ? 's' : ''}…
                  </div>
                )}

                {/* Hint (top-left, dismissible once a lock exists) */}
                {mapLoadingCount === 0 && lockedJobIds.size === 0 && mapJobDetails.length > 0 && (
                  <div className="absolute top-3 left-3 z-[1000] bg-white/90 rounded-xl border border-gray-100 shadow-sm px-3 py-1.5 text-xs text-gray-500 pointer-events-none">
                    Hover a route to highlight · double-click to lock
                  </div>
                )}

                <MapLegend
                  lockedCount={lockedJobIds.size}
                  freeCount={freeCount}
                  onJobCount={onJobCount}
                />
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
